<script setup lang="ts">
import FieldError from '@/application/components/ui/FieldError.vue';
import AppButton from '@/application/components/ui/AppButton.vue';
import { ElAlert } from 'element-plus';
import { NotificationVariant, useNotification } from '@/application/components/ui/useNotification';
import PhoneInput from '@/application/components/form-elements/PhoneInput/PhoneInput.vue';
import { ElCollapse, ElCollapseItem, ElForm, ElFormItem, ElInput } from 'element-plus';
import { vMaska } from 'maska/vue';
import { requestMaklerVollmacht } from "@/api/maklerVollmacht/maklerVollmachtApi";
import { useForm } from "@/api/lib/integration";
import { onMounted, ref } from "vue";
import { useBaseDataStore } from "@/user/store/baseDataStore";
import { useUserDataStore } from "@/user/store/userDataStore";
import { storeToRefs } from "pinia";

const emit = defineEmits(['cancel', 'success']);
const { userData } = storeToRefs(useUserDataStore());
const { duzen } = useBaseDataStore();
const invalidMobil = ref(false);

const { form, errors, submit, updateField, updateForm, loading } = useForm(
  requestMaklerVollmacht,
   {
    street: userData.value?.mainAddress?.street ?? '',
    houseNumber: userData.value?.mainAddress?.houseNumber ?? '',
    zipCode: userData.value?.mainAddress?.zipCode ? `${userData.value?.mainAddress?.zipCode}` : '',
    city: userData.value?.mainAddress?.city ?? '',
    mobile: userData.value?.mobile ?? '',
  },
  ({ status }) => {
    userData.value = {
      ...userData.value ?? {
        pwEmail: null,
        phone: null,
      },
      mainAddress: {
        street: form.value.street,
        city: form.value.city,
        houseNumber: form.value.houseNumber,
        zipCode: Number(form.value.zipCode),
      },
      mobile: form.value.mobile,
    };
    useBaseDataStore().maklervollmachtStatus = status;
    useNotification().show(
      `Der Maklervertrag bzw. -vollmacht wird vorbereitet. Dies kann einige Minuten dauern.`,
      NotificationVariant.Success,
    );
    emit('success');
  },
  () => {
    useNotification().show(
      `Möglicherweise sind noch nicht alle Voraussetzungen erfüllt. ${duzen('Versuche', 'Versuchen Sie')}
      es zu einem späteren Zeitpunkt erneut.`,
      NotificationVariant.Error,
    );
  },
);

const saveSubmit = async () => {
  if (invalidMobil.value) return;

  await submit();
}

onMounted(async() => {
  if (!useUserDataStore().userData?.mainAddress) {
    loading.value = true;
    await useUserDataStore().fetchUserContacts();
    updateForm({
      street: userData.value?.mainAddress?.street ?? form.value.street ?? '',
      houseNumber: userData.value?.mainAddress?.houseNumber ?? form.value.houseNumber ?? '',
      zipCode: userData.value?.mainAddress?.zipCode ? `${userData.value?.mainAddress?.zipCode}` : form.value.zipCode ?? '',
      city: userData.value?.mainAddress?.city ?? form.value.city ?? '',
      mobile: userData.value?.mobile ?? form.value.mobile ?? '',
    })
    loading.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col gap-3 justify-between h-full">
    <div class="flex flex-col gap-2 border border-gray-200 rounded-lg py-2 px-3 bg-white mb-auto">
      <p class="text-sm text-gray-800 mb-2">
        Damit {{ duzen('dein Berater dich', 'Ihr Berater Sie ') }} gegenüber den jeweiligen Versicherern vertreten
        kann, wird ein Maklervertrag bzw. -vollmacht benötigt. Diese {{ duzen('kannst du', 'können Sie') }} mit
        wenigen Klicks anfordern und unterschreiben. {{ duzen('Prüfe', 'Prüfen Sie') }} vorher bitte, ob
        {{ duzen('deine', 'Ihre') }} Kontaktdaten vollständig und aktuell sind. Wenn das Maklerdokument
        unterschrieben ist, {{ duzen('kannst du', 'können Sie') }} es bei den Dokumenten aufrufen.
      </p>
      <ElAlert type="info" class="pt-3 el-alert--primary" show-icon>
        <ElCollapse class="el-collapse--plain">
          <ElCollapseItem>
            <template #title>
              <h3 class="text-sm leading-5 font-semibold text-blue-800 -m-1 p-0">Wieso werden diese Daten benötigt?</h3>
            </template>
            <ul class="pt-2 space-y-2">
              <li>
                Die Angabe Ihrer Adresse wird für den Maklervertrag bzw. -vollmacht benötigt.
              </li>
              <li>
                Die Handynummer ist für den Identifikationsprozess der Unterschrift notwendig. Bei Aufruf des Dokuments
                {{ duzen('erhältst du', 'erhalten Sie') }} einen 6-stelligen Code per SMS. (Ihnen entstehen keine Kosten)
              </li>
            </ul>
          </ElCollapseItem>
        </ElCollapse>
      </ElAlert>
    </div>

    <ElForm label-position="top" require-asterisk-position="right">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-4 md:flex-row justify-between">
          <ElFormItem
            label="Straße"
            for="Straße"
            class="w-full"
            :error="errors.street?.[0]"
          >
            <ElInput id="Straße" :model-value="form.street" @update:model-value="updateField('street', $event)" />
          </ElFormItem>
          <ElFormItem
            label="Hausnummer"
            for="Hausnummer"
            class="w-full md:w-52"
            :error="errors.houseNumber?.[0]"
          >
            <ElInput id="Hausnummer" :model-value="form.houseNumber" @update:model-value="updateField('houseNumber', $event)" />
          </ElFormItem>
        </div>
        <div class="flex flex-col gap-4 md:flex-row justify-between">
          <ElFormItem
            label="Postleitzahl"
            for="Postleitzahl"
            class="w-full md:w-52"
            :error="errors.zipCode?.[0]"
          >
            <ElInput id="Postleitzahl" v-maska="'#####'" :model-value="form.zipCode" @update:model-value="updateField('zipCode', $event)" />
          </ElFormItem>
          <ElFormItem
            label="Stadt"
            for="Stadt"
            class="w-full"
            :error="errors.city?.[0]"
          >
            <ElInput id="Stadt" :model-value="form.city" @update:model-value="updateField('city', $event)" />
          </ElFormItem>
        </div>
        <ElFormItem
          label="Mobilfunknummer"
          for="Mobilfunknummer"
          :error="errors.mobile?.[0]"
        >
          <PhoneInput
            id="Mobilfunknummer"
            :model-value="form.mobile"
            mobile
            @update:model-value="updateField('mobile', $event)"
            @valid="invalidMobil = false"
            @invalid="invalidMobil = true"
          />
          <FieldError v-if="invalidMobil">Ungültiges Mobilfunknummer Format</FieldError>
        </ElFormItem>
      </div>
    </ElForm>

    <div class="sticky bottom-0 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <AppButton
        :disabled="invalidMobil"
        :handler="saveSubmit"
        class="w-full sm:ml-3 sm:w-auto"
        type="primary"
      >
        Jetzt anfordern
      </AppButton>
    </div>
  </div>
</template>
